/* =============================================================================
   Shared themed UI primitives for the SPA.
   All visuals derive from the CSS tokens in theme.css (var(--…)). Components here
   are the reusable building blocks the views compose. Built icon-ready so a later
   pass (Option 3) can swap text labels for icon-only buttons without a rewrite:
   <Btn> already accepts an `icon` + keeps an accessible label.
   ========================================================================== */
import React from 'react';

/* Token accessors for inline styles (mirror theme.css :root). Using var() keeps a single source
   of truth — change the token in theme.css and everything here follows. */
export const t = {
  bg: 'var(--bg)',
  surface: 'var(--surface)',
  surface2: 'var(--surface-2)',
  border: 'var(--border)',
  borderStrong: 'var(--border-strong)',
  text: 'var(--text)',
  muted: 'var(--text-muted)',
  faint: 'var(--text-faint)',
  accent: 'var(--accent)',
  accentInk: 'var(--accent-ink)',
  ok: 'var(--ok)',
  warn: 'var(--warn)',
  danger: 'var(--danger)',
  draft: 'var(--draft)',
  radius: 'var(--radius)',
  radiusSm: 'var(--radius-sm)',
  pill: 'var(--radius-pill)',
  mono: 'var(--font-mono)',
} as const;

export const BRAND_NAME = 'BridgeHouse Collaborate';

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const btnBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  fontFamily: 'inherit',
  fontWeight: 600,
  fontSize: 13,
  lineHeight: 1,
  padding: '8px 14px',
  borderRadius: t.pill,
  border: '1px solid transparent',
  cursor: 'pointer',
  transition: 'background 0.15s, border-color 0.15s, opacity 0.15s',
  whiteSpace: 'nowrap',
};

const btnVariants: Record<BtnVariant, React.CSSProperties> = {
  primary: { background: t.text, color: '#000', borderColor: t.text },
  secondary: { background: 'rgba(255,255,255,0.08)', color: t.text, borderColor: t.borderStrong },
  ghost: { background: 'transparent', color: t.muted, borderColor: 'transparent' },
  danger: { background: 'transparent', color: t.danger, borderColor: 'transparent' },
};

/**
 * Button. Icon-ready: pass `icon` (a node) and optionally set `iconOnly` to render just the icon
 * while keeping the text as the accessible name (aria-label). Today most buttons show text; a later
 * Option-3 pass can set iconOnly for transport controls etc. without touching call sites' semantics.
 */
export function Btn({
  variant = 'secondary',
  icon,
  iconOnly = false,
  children,
  style,
  ...rest
}: {
  variant?: BtnVariant;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  children?: React.ReactNode;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const label = typeof children === 'string' ? children : undefined;
  return (
    <button
      {...rest}
      aria-label={iconOnly ? (rest['aria-label'] ?? label) : rest['aria-label']}
      style={{ ...btnBase, ...btnVariants[variant], ...(rest.disabled ? { opacity: 0.45, cursor: 'not-allowed' } : {}), ...style }}
    >
      {icon}
      {!iconOnly && children}
    </button>
  );
}

/** A surface card with hairline border. `hover` adds a subtle lift affordance for clickable cards. */
export function Card({
  hover = false,
  style,
  children,
  ...rest
}: { hover?: boolean; children: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...rest}
      style={{
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: t.radius,
        padding: 16,
        ...(hover ? { cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s' } : {}),
        ...style,
      }}
      onMouseEnter={hover ? (e) => { (e.currentTarget as HTMLDivElement).style.borderColor = t.borderStrong; } : rest.onMouseEnter}
      onMouseLeave={hover ? (e) => { (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(255,255,255,0.10)'; } : rest.onMouseLeave}
    >
      {children}
    </div>
  );
}

export type BadgeTone = 'ok' | 'warn' | 'danger' | 'draft' | 'accent' | 'neutral';

const badgeTone: Record<BadgeTone, { fg: string; bd: string }> = {
  ok: { fg: t.ok, bd: 'rgba(76,195,138,0.4)' },
  warn: { fg: t.warn, bd: 'rgba(224,165,75,0.4)' },
  danger: { fg: t.danger, bd: 'rgba(229,103,91,0.4)' },
  draft: { fg: t.faint, bd: t.border },
  accent: { fg: t.accent, bd: 'rgba(77,184,184,0.4)' },
  neutral: { fg: t.muted, bd: t.border },
};

/** Small status pill (approved / changes / draft / pending counts, etc.). */
export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: React.ReactNode }) {
  const c = badgeTone[tone];
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
        color: c.fg, border: `1px solid ${c.bd}`, borderRadius: t.pill,
        padding: '2px 8px', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
}

/** Section heading with an optional accent icon + a trailing slot (e.g. an action/count). */
export function SectionTitle({
  icon, children, trailing, accent = t.text,
}: { icon?: React.ReactNode; children: React.ReactNode; trailing?: React.ReactNode; accent?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 12px' }}>
      {icon && <span aria-hidden style={{ color: accent, display: 'inline-flex' }}>{icon}</span>}
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: t.text }}>{children}</h3>
      {trailing && <span style={{ marginLeft: 'auto' }}>{trailing}</span>}
    </div>
  );
}

/** Muted helper/caption text. */
export function Muted({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <span style={{ color: t.muted, fontSize: 12, ...style }}>{children}</span>;
}

/** One row in the shortcuts help: the key(s) to show, and what they do. */
export interface ShortcutRow {
  keys: string[];   // e.g. ['Space'] or ['Shift', '←']
  label: string;    // e.g. 'Play / pause'
}

/** A small key cap, rendered in the shortcuts help overlay. */
function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      style={{
        display: 'inline-block', minWidth: 20, textAlign: 'center',
        padding: '2px 7px', fontSize: 12, fontFamily: t.mono, lineHeight: 1.4,
        color: t.text, background: t.surface2,
        border: `1px solid ${t.borderStrong}`, borderRadius: 6,
      }}
    >
      {children}
    </kbd>
  );
}

/**
 * Keyboard-shortcuts help overlay. A centered modal listing the active shortcuts; dismissed by its
 * close button, a backdrop click, or Esc (the caller wires Esc). Rendered only when `open`.
 */
export function ShortcutsHelp({
  open, onClose, rows,
}: { open: boolean; onClose: () => void; rows: ShortcutRow[] }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420, background: t.surface,
          border: `1px solid ${t.border}`, borderRadius: t.radius, padding: 20,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <strong style={{ fontSize: 15 }}>Keyboard shortcuts</strong>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'none', color: t.muted, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}
          >
            ×
          </button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ padding: '6px 0', width: '45%' }}>
                  {r.keys.map((k, j) => (
                    <span key={j}>
                      {j > 0 && <span style={{ color: t.faint, margin: '0 3px' }}>+</span>}
                      <Kbd>{k}</Kbd>
                    </span>
                  ))}
                </td>
                <td style={{ padding: '6px 0', fontSize: 13, color: t.muted }}>{r.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --- Minimal inline icons (currentColor stroke) — used sparingly in Option 2, ready for Option 3.
   Kept as tiny SVGs so there's no icon-library dependency. --- */
const ico = (path: React.ReactNode, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{path}</svg>
);
// A filled-glyph helper for solid shapes (play/pause/stop read better filled at small sizes).
const icoFill = (path: React.ReactNode, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden>{path}</svg>
);
export const Icon = {
  mix: () => ico(<><path d="M4 12h16" /><circle cx="9" cy="12" r="2" /><circle cx="15" cy="12" r="2" /></>),
  stems: () => ico(<><line x1="6" y1="4" x2="6" y2="20" /><line x1="12" y1="4" x2="12" y2="20" /><line x1="18" y1="4" x2="18" y2="20" /></>),
  folder: () => ico(<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />),
  members: () => ico(<><circle cx="9" cy="8" r="3" /><path d="M3 20a6 6 0 0 1 12 0" /><path d="M16 5a3 3 0 0 1 0 6" /><path d="M21 20a6 6 0 0 0-4-5.6" /></>),
  chevron: () => ico(<path d="M9 6l6 6-6 6" />),
  plus: () => ico(<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>),
  // --- Transport + actions (Option 3) ---
  play: () => icoFill(<path d="M8 5v14l11-7z" />),
  pause: () => icoFill(<><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></>),
  stop: () => icoFill(<rect x="6" y="6" width="12" height="12" rx="1.5" />),
  loop: () => ico(<><path d="M17 2l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>),
  download: () => ico(<><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></>),
  check: () => ico(<path d="M20 6L9 17l-5-5" />),
  x: () => ico(<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>),
  mute: () => ico(<><path d="M11 5L6 9H2v6h4l5 4z" /><line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" /></>),
  solo: () => ico(<><path d="M11 5L6 9H2v6h4l5 4z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M19 5a9 9 0 0 1 0 14" /></>),
  edit: () => ico(<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></>),
  scissors: () => ico(<><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><line x1="20" y1="4" x2="8.12" y2="15.88" /><line x1="14.47" y1="14.48" x2="20" y2="20" /><line x1="8.12" y1="8.12" x2="12" y2="12" /></>),
};
